"use client";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, Button, CircularProgress, IconButton, Alert } from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useState, useEffect } from "react";
import axios from "axios";

import { useMigrationDetails } from "../hooks/useMigrationDetails";
import AdminLayout from "../components/layout/AdminLayout";
import Summary from "../components/specific/MigratiionSetup/Summary/Summary";
import { useAccountId } from "../hooks/useAccountId";
import { API_BASE_URL } from "../services/api/endpoints";
import { downloadBase64AsZip } from "../utils/file-utils";
import { downloadContent } from "../utils/file-utils";

export default function MigrationDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Usar el hook useAccountId para obtener el accountId
  const accountId = useAccountId();
  const [accountIdLoading, setAccountIdLoading] = useState(!accountId);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadSuccess, setDownloadSuccess] = useState<string | null>(null);

  const { migrationData, loading, error } = useMigrationDetails({
    migrationId: id || "",
    autoFetch: !!accountId, // Solo hacer fetch automático si hay accountId
  });

  // Efecto para actualizar el estado de carga del accountId
  useEffect(() => {
    if (accountId) {
      setAccountIdLoading(false);
    }
  }, [accountId]);

  const handleGoBack = () => {
    navigate("/migrations");
  };

  // Modificar la función handleDownload para manejar el nuevo tipo MIGRATION
  const handleDownload = async (fileTypeParam: string, fileName?: string) => {
    try {
      console.log(
        `handleDownload - Iniciando descarga: ${fileTypeParam}, sourceFileName: ${fileName || "no proporcionado"}`
      );
      setIsDownloading(true);
      setDownloadError(null);
      setDownloadSuccess(null);

      if (!id || !accountId) {
        const errorMsg = "Faltan datos necesarios para la descarga (ID de migración o Account ID)";
        console.error(errorMsg);
        setDownloadError(errorMsg);
        return;
      }

      // Verificar si tenemos datos de migración
      if (!migrationData) {
        const errorMsg = "No hay datos de migración disponibles";
        console.error(errorMsg);
        setDownloadError(errorMsg);
        return;
      }

      try {
        let url = `${API_BASE_URL}/api/v1/migrations/${id}/files/${encodeURIComponent(
          fileName!
        )}?file_type=${fileTypeParam}`;

        console.log(`handleDownload - URL de descarga: ${url}`);

        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
          responseType: "json",
          validateStatus: () => true,
        });

        if (response.status >= 200 && response.status < 300) {
          if (fileName!.includes(".zip")) {
            await downloadBase64AsZip(response.data.content, fileName!);
          } else {
            await downloadContent(response.data.content, fileName!);
          }

          const successMsg = `Archivo ${fileName} descargado correctamente`;
          setDownloadSuccess(successMsg);
        } else {
          throw new Error(`Error en solicitud directa: Status ${response.status}`);
        }
      } catch (error) {
        const errorMsg = `Error descargando archivo: ${error instanceof Error ? error.message : String(error)}`;
        console.error(errorMsg);
        setDownloadError(errorMsg);
      }
    } catch (error) {
      const errorMsg = `Error general: ${error instanceof Error ? error.message : String(error)}`;
      console.error(errorMsg);
      setDownloadError(errorMsg);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleBackToSetup = () => {
    navigate("/migrate");
  };

  if (!accountId) {
    return (
      <AdminLayout>
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" color="error">
            Account ID not found. Please log in again.
          </Typography>
          <Button variant="contained" onClick={() => navigate("/login")} sx={{ mt: 2 }}>
            Go to login
          </Button>
        </Box>
      </AdminLayout>
    );
  }

  if (accountIdLoading) {
    return (
      <AdminLayout>
        <Box sx={{ p: 3, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Cargando información de cuenta...</Typography>
        </Box>
      </AdminLayout>
    );
  }

  const content = (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          pb: 2,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start" }}>
          <IconButton onClick={handleGoBack} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="h6">{migrationData?.content?.name || "Project Name"}</Typography>
            <Typography variant="body2" color="grey.600">
              {migrationData?.content?.migrationId || "N/A"}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Mensaje de éxito */}
      {downloadSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {downloadSuccess}
        </Alert>
      )}

      {/* Error de descarga */}
      {downloadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error de descarga: {downloadError}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box>
          <Typography color="error">{error}</Typography>
          <Button variant="contained" onClick={handleGoBack} sx={{ mt: 2 }}>
            Volver a la lista
          </Button>
        </Box>
      ) : (
        <Summary dataResponse={migrationData} onDownload={handleDownload} onBackToSetup={handleBackToSetup} />
      )}
    </Box>
  );

  // Renderizar el contenido dentro del AdminLayout
  return <AdminLayout>{content}</AdminLayout>;
}
